import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { LoginResponse, UserResponse } from './types';

export interface AuthState {
  token: string | null;
  user: UserResponse | null;
  isAuthenticated: boolean;
  initialized: boolean;
}

/**
 * localStorage is only a cache. A cached token/user must not authorize UI by
 * itself: the backend is the source of truth for status, role and tenant.
 */
const loadAuthState = (): AuthState => {
  try {
    const token = localStorage.getItem('token');
    const userString = localStorage.getItem('user');
    let user: UserResponse | null = null;

    if (userString && userString !== 'undefined' && userString !== '{}') {
      try {
        user = JSON.parse(userString);
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return { user: null, token: null, isAuthenticated: false, initialized: true };
      }
    }

    if (token && user) {
      return { user, token, isAuthenticated: false, initialized: false };
    }

    return { user: null, token: null, isAuthenticated: false, initialized: true };
  } catch {
    return { user: null, token: null, isAuthenticated: false, initialized: true };
  }
};

const initialState: AuthState = loadAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<LoginResponse>) => {
      const { token, data: user } = action.payload;
      if (!token || !user) {
        return;
      }
      state.token = token;
      state.user = user;
      state.isAuthenticated = true;
      state.initialized = true;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    },
    logout: state => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.initialized = true;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    initializeAuth: state => {
      // With no cached token there is nothing to verify remotely.
      if (!state.token) {
        state.user = null;
        state.isAuthenticated = false;
        state.initialized = true;
      }
    },
    setVerifiedUser: (state, action: PayloadAction<UserResponse>) => {
      if (!state.token) {
        state.user = null;
        state.isAuthenticated = false;
        state.initialized = true;
        return;
      }
      state.user = action.payload;
      state.isAuthenticated = true;
      state.initialized = true;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    setCurrentUser: (state, action: PayloadAction<UserResponse>) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(state.user));
    },
  },
});

export const {
  setCredentials,
  logout,
  initializeAuth,
  setVerifiedUser,
  setCurrentUser,
} = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectCurrentToken = (state: RootState) => state.auth.token;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsInitialized = (state: RootState) => state.auth.initialized;
